import { GenericAbortSignal } from 'axios';

import http from 'services/http';

// Per-(client, source system) portal automation config: the retrieval-mode
// ladder and the script sharing level. Portal credentials for the EXTENSION flow
// live only in the extension's credential store. Headless credentials (OLIO-2886)
// are the deliberate exception: they are envelope-encrypted in the browser
// (services/crypto/credentialEnvelope) and stored server-side as ciphertext no
// Olio server can decrypt — plaintext still never reaches a server.

export type RetrievalMode = 'manual' | 'extension' | 'headless';

// How widely recorded scripts are shared, for this source system only. inter_client
// is reciprocal: the owning client must be inter_client for the same system too.
export type ScriptSharing = 'own_only' | 'client' | 'inter_client';

export interface PortalAutomationSettingOptions {
  id: string;
  clientId: string;
  sourceSystem: string;
  retrievalMode: RetrievalMode;
  scriptSharing: ScriptSharing;
  createdAt: string;
  updatedAt: string;
  // Headless credential mirror (OLIO-2881) — cached from the integration
  // platform, which owns the credentials; all null until a sign-in is saved.
  // status 'active' with a null verifiedAt means "saved, never checked" — the
  // verify job stamps verifiedAt when a login is observed to work.
  headlessCredentialsStatus?: 'active' | 'invalid' | null;
  headlessCredentialsUsername?: string | null;
  headlessUserId?: string | null;
  headlessCredentialsSavedAt?: string | null;
  headlessCredentialsVerifiedAt?: string | null;
}

export interface PortalAutomationSettingsIndex {
  data: PortalAutomationSettingOptions[];
}

export const portalAutomationSettingQueryKeys = {
  base: ['portalAutomationSettings'] as const,
  list: (clientId: string) => ['portalAutomationSettings', 'list', clientId] as const,
};

export async function indexPortalAutomationSettings(clientId: string, signal?: GenericAbortSignal) {
  return http
    .get<PortalAutomationSettingsIndex>(`admin/portal_automation_settings`, { params: { client_id: clientId }, signal })
    .then((res) => res.data);
}

export type UpsertPortalAutomationSettingInput = {
  // Top-level field keys are camelCase; OliveBranch snake_cases them on the API.
  clientId: string;
  sourceSystem: string;
  retrievalMode?: RetrievalMode;
  scriptSharing?: ScriptSharing;
};

export async function upsertPortalAutomationSetting(input: UpsertPortalAutomationSettingInput) {
  return http.post<PortalAutomationSettingOptions>(`admin/portal_automation_settings`, input).then((res) => res.data);
}
